import type { Me } from '@cabgo/shared';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiGet, apiPost, getTokens, SESSION_EXPIRED_EVENT, setActiveOrg, setTokens } from './api';
import { unregisterWebPush } from './webpush';

interface AuthState {
  me: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({
  children,
  onSessionCleared,
}: {
  children: ReactNode;
  onSessionCleared?: () => void;
}) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const clearSessionCache = useCallback(() => onSessionCleared?.(), [onSessionCleared]);

  useEffect(() => {
    if (!getTokens()) {
      setLoading(false);
      return;
    }
    apiGet<Me>('/me')
      .then(setMe)
      .catch(() => {
        setTokens(null);
        clearSessionCache();
      })
      .finally(() => setLoading(false));
  }, [clearSessionCache]);

  // Fired by lib/api.ts when a 401 can't be recovered by a token refresh —
  // dropping `me` makes RequireAuth redirect to /login.
  useEffect(() => {
    const onExpired = () => {
      clearSessionCache();
      setMe(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [clearSessionCache]);

  const login = useCallback(
    async (email: string, password: string) => {
      const tokens = await apiPost<{ accessToken: string; refreshToken: string }>('/auth/org/login', {
        email,
        password,
      });
      clearSessionCache();
      setTokens(tokens);
      setMe(await apiGet<Me>('/me'));
    },
    [clearSessionCache],
  );

  const logout = useCallback(async () => {
    const tokens = getTokens();
    if (tokens) {
      await unregisterWebPush();
      await apiPost('/auth/logout', { refreshToken: tokens.refreshToken }).catch(() => {});
    }
    setTokens(null);
    setActiveOrg(null);
    clearSessionCache();
    setMe(null);
  }, [clearSessionCache]);

  return <AuthContext.Provider value={{ me, loading, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** ADMIN-level write access — SUPER_ADMIN passes everywhere an ADMIN does. */
export function isAdminRole(role?: string): boolean {
  return role === 'ADMIN' || role === 'SUPER_ADMIN';
}
export function hasCapability(me: Me | null, capability: string): boolean {
  return Boolean(me?.capabilities?.includes(capability));
}

/** MANAGER-level write access (driver approval/editing) — ADMIN and up pass too. */
export function isManagerRole(role?: string): boolean {
  return role === 'MANAGER' || isAdminRole(role);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!me) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}
